/**
 * 指導計画 (teaching plan)
 *
 * ラベル・配色はデザインモック「指導計画 20260808」準拠。
 * APIの契約は chaild-api の docs/architecture/teaching-plan/api-design.md (契約v2) を参照。
 */
import {
  NurseryTeachingPlanCellType,
  NurseryTeachingPlanListTabType,
  NurseryTeachingPlanStatusType,
  NurseryTeachingPlanTransferRuleModeType,
  NurseryTeachingPlanType,
} from "chaild-api/lib";

/** 帳票種別の選択肢 (一覧フィルタ用。空文字=すべて) */
export const planTypeItems: { title: string; value: NurseryTeachingPlanType | "" }[] = [
  { title: "すべて", value: "" },
  { title: "全体的な計画", value: "overall" },
  { title: "年間指導計画", value: "annual" },
  { title: "月案", value: "monthly" },
  { title: "週案・日案", value: "weekly" },
];

/** 帳票種別の表示名 */
export const planTypeLabels: Record<NurseryTeachingPlanType, string> = {
  overall: "全体的な計画",
  annual: "年間指導計画",
  monthly: "月案",
  weekly: "週案・日案",
};

/** 新規作成モーダルの帳票種別 (「すべて」を含まない) */
export const createPlanTypeItems: { title: string; value: NurseryTeachingPlanType }[] = [
  { title: "全体的な計画", value: "overall" },
  { title: "年間指導計画", value: "annual" },
  { title: "月案", value: "monthly" },
  { title: "週案・日案", value: "weekly" },
];

/** 帳票種別の並び順 (上位→下位。レイアウト一覧・転記ルールの表示順に使う) */
export const planTypeOrder: NurseryTeachingPlanType[] = [
  "overall",
  "annual",
  "monthly",
  "weekly",
];

/**
 * 隣接上位の帳票種別 (転記ルールは隣接ペアのみ許可。api-design.md §2.6)。
 * サーバ側 TEACHING_PLAN_ADJACENT_UPPER と対応させる。
 */
export const adjacentUpperPlanType: Record<
  NurseryTeachingPlanType,
  NurseryTeachingPlanType | null
> = {
  overall: null,
  annual: "overall",
  monthly: "annual",
  weekly: "monthly",
};

/** ステータスタブ (件数バッジはAPIのstatusCountsを参照) */
export const statusTabs: {
  value: NurseryTeachingPlanListTabType;
  title: string;
  countKey: "all" | "awaitingApproval" | "returned" | "draft" | "approved";
}[] = [
  { value: "all", title: "すべて", countKey: "all" },
  { value: "awaitingApproval", title: "承認待ち", countKey: "awaitingApproval" },
  { value: "returned", title: "差戻し・要対応", countKey: "returned" },
  { value: "draft", title: "下書き", countKey: "draft" },
  { value: "approved", title: "承認済み", countKey: "approved" },
];

/** ステータスバッジ (モックの配色をそのまま使う) */
export const statusStyles: Record<
  NurseryTeachingPlanStatusType,
  { label: string; background: string; color: string }
> = {
  draft: { label: "下書き", background: "#eceff1", color: "#546e7a" },
  submitted: { label: "提出済み", background: "#e3f2fd", color: "#1565c0" },
  reviewing: { label: "確認中", background: "#fff3e0", color: "#ef6c00" },
  firstApproved: { label: "一次承認済み", background: "#e0f7fa", color: "#00838f" },
  returned: { label: "差戻し", background: "#ffebee", color: "#c62828" },
  resubmitted: { label: "再提出済み", background: "#ede7f6", color: "#5e35b1" },
  approved: { label: "承認済み", background: "#e8f5e9", color: "#2e7d32" },
};

/** 提出期限を赤字で強調するステータス (未提出扱いのもの) */
export const deadlineAlertStatuses: NurseryTeachingPlanStatusType[] = ["draft", "returned"];

/** 一括Excel出力の上限件数 (APIと揃える) */
export const bulkExportLimit = 50;

/** 一括Excel出力の完了待ちポーリング間隔 (ms) */
export const exportPollIntervalMs = 3000;

/** ポーリングの打ち切り回数 (約5分) */
export const exportPollMaxCount = 100;

/** セル値upsertの1リクエスト上限 (APIと揃える) */
export const valueUpsertLimit = 50;

/** 自動保存のデバウンス時間 (ms) */
export const autoSaveDebounceMs = 800;

/**
 * セル型 (レイアウトビルダー用)。label / reference は入力セルではない。
 */
export const cellTypeLabels: Record<NurseryTeachingPlanCellType, string> = {
  label: "見出し",
  longText: "長文",
  shortText: "短文",
  select: "選択肢",
  date: "日付",
  number: "数値",
  reference: "参照",
};

/**
 * セル値の文字数上限 (chaild-api の validateTeachingPlanCellValue と揃える)。
 *
 * 超えるとサーバが400で弾くが、自動保存は silentError で走るため理由が表示されない。
 * 画面側で先に止めて理由を出さないと、ツールバーに「保存に失敗しました」だけが
 * 残り、再試行しても直らず、離脱も beforeunload に止められて詰む。
 */
export const cellValueMaxLengths: Partial<Record<NurseryTeachingPlanCellType, number>> = {
  longText: 10000,
  shortText: 200,
  number: 20,
};

export const cellTypeItems: { title: string; value: NurseryTeachingPlanCellType }[] = [
  { title: "見出し", value: "label" },
  { title: "長文", value: "longText" },
  { title: "短文", value: "shortText" },
  { title: "選択肢", value: "select" },
  { title: "日付", value: "date" },
  { title: "数値", value: "number" },
  { title: "参照", value: "reference" },
];

/** 転記モード。reference=常に上位の値を映す / carry=作成時に引き込んで以後は独立 */
export const transferModeLabels: Record<NurseryTeachingPlanTransferRuleModeType, string> = {
  reference: "参照",
  carry: "引き込み",
};

export const transferModeItems: {
  title: string;
  value: NurseryTeachingPlanTransferRuleModeType;
  description: string;
}[] = [
  {
    title: "参照",
    value: "reference",
    description: "上位計画の値を常に映します。転記先では編集できません。",
  },
  {
    title: "引き込み",
    value: "carry",
    description: "作成時・転記実行時に値をコピーします。以後は個別に編集できます。",
  },
];

/** 参照セルの解決状態の表示 */
export const referenceResolutionLabels: Record<
  "resolved" | "ruleMissing" | "ruleBroken" | "sourceMissing",
  string
> = {
  resolved: "参照中",
  ruleMissing: "転記ルール未設定",
  ruleBroken: "転記ルール要確認",
  sourceMissing: "参照元の帳票が未作成",
};

/**
 * sourceMissing は「上位の帳票が見つからない」場合と
 * 「帳票はあるが、そのレイアウトにこの項目が無い（＝転記元はレイアウトを固定しないため、
 * 上位が別レイアウトで作られていると起きる）」場合の両方を含む。
 * サーバは前者では転記元帳票を返せないので、その有無で理由を出し分ける。
 */
export const resolutionLabelOf = (
  resolution: "resolved" | "ruleMissing" | "ruleBroken" | "sourceMissing",
  hasSourcePlan: boolean
): string => {
  if (resolution === "sourceMissing" && hasSourcePlan) {
    return "転記元の帳票にこの項目がありません";
  }
  return referenceResolutionLabels[resolution];
};

/** 提出期限ルールの種別 */
export const deadlineRuleKindItems: {
  title: string;
  value: "none" | "dayOfPreviousMonth" | "weekdayOfPreviousWeek" | "fixedDate";
  hint: string;
}[] = [
  { title: "期限なし", value: "none", hint: "" },
  {
    title: "前月のN日",
    value: "dayOfPreviousMonth",
    hint: "月案向け。対象月の前月N日を期限にします。",
  },
  {
    title: "前週の曜日",
    value: "weekdayOfPreviousWeek",
    hint: "週案向け。開始日が属する週の前週の指定曜日を期限にします。",
  },
  {
    title: "年度内の固定日",
    value: "fixedDate",
    hint: "全体的な計画・年間指導計画向け。年度内の固定日を期限にします。",
  },
];

/** 曜日 (0=日) */
export const weekdayItems: { title: string; value: number }[] = [
  { title: "日曜日", value: 0 },
  { title: "月曜日", value: 1 },
  { title: "火曜日", value: 2 },
  { title: "水曜日", value: 3 },
  { title: "木曜日", value: 4 },
  { title: "金曜日", value: 5 },
  { title: "土曜日", value: 6 },
];

/** 文例本文の最大文字数 (APIと揃える) */
export const phraseBodyMaxLength = 2000;

/** コメント本文の最大文字数 (APIと揃える) */
export const commentBodyMaxLength = 2000;

/** レイアウト名の最大文字数 (APIと揃える。論理削除時のサフィックス分を残した値) */
export const templateNameMaxLength = 90;
